import { useEffect, useState } from "react";

import { faCircleMinus, faCirclePlus } from "@fortawesome/pro-regular-svg-icons";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import type { UserCourseExercise } from "api/schemas/UserCourse";
import { useLocale } from "utils/date/useLocale";

import { IconLink } from "./IconLink";
import { RadioButtons } from "./RadioButtons";
import { OuterMargin, TopBar, TopLeft, TopRight } from "./SharedStyledComponents";
import { UserCourseExerciseComponent } from "./UserCourseExercise";

const weekSort = (locale?: string) => (a: UserCourseExercise, b: UserCourseExercise) => {
  if (a.introduction_protocol_week === b.introduction_protocol_week) {
    return a.levels[a.current_level - 1].title.localeCompare(b.levels[b.current_level - 1].title, locale, {
      numeric: true,
      sensitivity: "base",
    });
  }
  return a.introduction_protocol_week >= b.introduction_protocol_week ? 1 : -1;
};

export const ExercisesList = ({
  exercises,
  onOpenExercisePicker,
  currentProtocolWeek,
  onDeleteExercise,
  onChangeLevel,
  onChangeWeek,
  onChangeEndWeek,
  onClickExercise,
  newlyAddedExercises,
  onClearExercises,
  showActiveSelector,
}: {
  exercises: UserCourseExercise[];
  onOpenExercisePicker: () => void;
  onDeleteExercise: (id: number) => void;
  currentProtocolWeek?: number;
  onChangeLevel: (id: number, level: number) => void;
  onChangeWeek: (id: number, week: number) => void;
  onChangeEndWeek: (id: number, week: number | null) => void;
  onClickExercise: (n: number) => void;
  newlyAddedExercises: number[];
  onClearExercises: () => void;
  showActiveSelector: boolean;
}) => {
  type ActiveFilterValue = "all" | "active" | "future";

  const { t } = useTranslation();
  const [filteredExercises, setFilteredExercises] = useState<UserCourseExercise[]>([]);
  const [activeFilter, setActiveFilter] = useState<ActiveFilterValue>("all");
  const locale = useLocale().code;

  const filterOptions: { label: string; value: ActiveFilterValue }[] = [
    {
      label: t("usercourse.exerciselist.all"),
      value: "all",
    },
    {
      label: t("usercourse.exerciselist.active"),
      value: "active",
    },
    {
      label: t("usercourse.exerciselist.future"),
      value: "future",
    },
  ];

  useEffect(() => {
    const sortedExcercises = exercises.slice().sort(weekSort(locale));
    if (activeFilter === "all") {
      setFilteredExercises(sortedExcercises);
    }
    if (activeFilter === "active") {
      setFilteredExercises(sortedExcercises.filter(e => e.active));
    } else if (activeFilter === "future") {
      setFilteredExercises(sortedExcercises.filter(e => !e.active));
    }
  }, [exercises, activeFilter]);

  return (
    <div style={{ width: "100%" }}>
      <PanelBg>
        <OuterMargin>
          <TopBar>
            <TopLeft>
              {showActiveSelector && (
                <RadioButtons
                  options={filterOptions}
                  onChange={v => {
                    setActiveFilter(v);
                  }}
                  value={activeFilter}
                />
              )}
            </TopLeft>
            <TopRight>
              <IconLink
                icon={faCircleMinus}
                title={t("usercourse.remove_all_exercises.cta")}
                onClick={onClearExercises}
              />
              <IconLink icon={faCirclePlus} title={t("usercourse.add_exercise")} onClick={onOpenExercisePicker} />
            </TopRight>
          </TopBar>
          {filteredExercises && (
            <>
              <Exercises>
                {filteredExercises.map(exercise => {
                  return (
                    <UserCourseExerciseComponent
                      newlyAdded={newlyAddedExercises.includes(exercise.id)}
                      key={`usercourseexercise_${exercise.id}`}
                      exercise={exercise}
                      onChangeLevel={onChangeLevel}
                      onDelete={onDeleteExercise}
                      currentProtocolWeek={currentProtocolWeek}
                      onChangeWeek={onChangeWeek}
                      onChangeEndWeek={onChangeEndWeek}
                      onClickExercise={onClickExercise}
                      showActiveTag={showActiveSelector}
                    />
                  );
                })}
              </Exercises>
            </>
          )}
        </OuterMargin>
      </PanelBg>
    </div>
  );
};

const Exercises = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: stretch;
`;

const PanelBg = styled.div`
  padding: 16px 0;
  flex-wrap: wrap;
  gap: 16px;
`;
